import { Injectable } from '@angular/core';
import { Event } from '@api/models/event/event';
import { EventsHttpService } from '@api/http/events/events-http.service';
import { formatDate } from '@angular/common';

interface IEventsGroup {
  name: string;
  events: Event[];
}

@Injectable()
export class GameListService {
  public eventGroups: IEventsGroup[] = [];
  private isGrouped = true;

  constructor(private eventsHttpService: EventsHttpService) {
    this.sortEventsByStatusAndTimestamp =
      this.sortEventsByStatusAndTimestamp.bind(this);
  }

  public init(
    rawEvents: Partial<Event>[],
    groupByRound = false,
    groupByLeague = false,
    sortByLive = false,
    groupByDate = false,
    limit?: number
  ): void {
    let events = rawEvents.map((ev) => new Event(ev));
    if (limit) {
      events = events?.slice(0, limit);
    }
    this.isGrouped = true;
    this.eventGroups = [];
    if (groupByRound) {
      this.groupEventsByRound(events);
    } else if (groupByLeague) {
      this.groupEventsByLeague(events);
    } else if (groupByDate) {
      this.groupEventsByDate(events);
    } else {
      this.isGrouped = false;
      if (sortByLive) {
        events = events.sort(this.sortEventsByStatusAndTimestamp);
      }
      this.eventGroups = [{ name: '', events }];
    }
  }

  public async reloadGame(event: Event): Promise<void> {
    if (!this.eventGroups || this.eventGroups.length === 0) {
      return;
    }
    const updatedEvent = await this.eventsHttpService.getEvent(
      event._id,
      false
    );
    let group = this.eventGroups[0];
    if (this.isGrouped) {
      group = this.eventGroups.find(
        (group) => group.name === event.round.ui_name
      );
    }
    const eventIndex = group.events.findIndex(
      (exEvent) => exEvent._id === event._id
    );
    group.events[eventIndex] = new Event(updatedEvent);
    group.events = group.events.sort(this.sortEventsByStatusAndTimestamp);
  }

  private groupEventsByRound(events: Event[]): void {
    events?.forEach((ev) => {
      const existingGroup = this.eventGroups.find(
        (group) => group.name === ev.round.ui_name
      );
      if (existingGroup) {
        const existingEvent = existingGroup.events.find(
          (exEvent) => exEvent._id === ev._id
        );
        if (!existingEvent) {
          existingGroup.events.push(ev);
        }
        return;
      }
      this.eventGroups.push({
        name: ev.round.ui_name,
        events: [ev],
      });
    });
  }

  private groupEventsByLeague(events: Event[]): void {
    events?.forEach((event) => {
      const existingGroup = this.eventGroups.find(
        (group) => group.name === event.leagueid
      );
      if (existingGroup) {
        const existingEvent = existingGroup.events.find(
          (exEvent) => exEvent._id === event._id
        );
        if (!existingEvent) {
          existingGroup.events.push(event);
        }
        return;
      }
      this.eventGroups.push({
        name: event.leagueid,
        events: [event],
      });
    });
  }

  private groupEventsByDate(events: Event[]): void {
    events?.forEach((event) => {
      const startDate = formatDate(
        event.timestamps.start,
        'dd.MM.YYYY',
        'de-DE'
      );
      const existingGroup = this.eventGroups.find(
        (group) => group.name === startDate
      );
      if (existingGroup) {
        const existingEvent = existingGroup.events.find(
          (exEvent) => exEvent._id === event._id
        );
        if (!existingEvent) {
          existingGroup.events.push(event);
        }
        return;
      }
      this.eventGroups.push({
        name: startDate,
        events: [event],
      });
    });
  }

  private sortEventsByStatusAndTimestamp(a: Event, b: Event): number {
    if (this.isAustrianGame(a) && a.isActive()) {
      return -1;
    }
    if (this.isAustrianGame(b) && b.isActive()) {
      return 1;
    }
    if (a.isActive() && !b.isActive()) {
      return -1;
    }
    if (!a.isActive() && b.isActive()) {
      return 1;
    }
    return a.timestamps.start - b.timestamps.start;
  }

  private isAustrianGame(event: Event): boolean {
    return (
      event.teams.home.name.includes('Österreich') ||
      event.teams.away.name.includes('Österreich')
    );
  }
}
